// Keep this file identical in:
// - mobile/apps/grove-app/src/fruit-slices/utils/capabilityEndpoints.ts
// - fruitful-all/apps/serverless-functions/fruit-slice-listener-runner/sdk/capabilityEndpoints.ts

export type CapabilityMethod = 'GET' | 'POST';

export interface CapabilityRequest {
    method: CapabilityMethod;
    path: string;
    body?: unknown;
}

// A resolver returning a bare path describes a GET with no body.
export type CapabilityEndpointResolver = (
    params?: unknown,
) => string | CapabilityRequest;

export const toCapabilityRequest = (
    resolved: string | CapabilityRequest,
): CapabilityRequest =>
    typeof resolved === 'string' ? { method: 'GET', path: resolved } : resolved;

const getParam = (params: unknown, key: string): unknown => {
    if (params === null || typeof params !== 'object') {
        return undefined;
    }
    return Reflect.get(params, key) as unknown;
};

const reqRaw = (params: unknown, key: string): string => {
    const value = getParam(params, key);
    if (typeof value !== 'string' || value === '') {
        throw new Error(`missing required param: ${key}`);
    }
    return value;
};

const reqStr = (params: unknown, key: string): string =>
    encodeURIComponent(reqRaw(params, key));

const optNum = (params: unknown, key: string, fallback: number): number => {
    const value = getParam(params, key);
    return typeof value === 'number' ? value : fallback;
};

const optStr = (params: unknown, key: string): string | undefined => {
    const value = getParam(params, key);
    return typeof value === 'string' && value !== '' ? value : undefined;
};

const reqNum = (params: unknown, key: string): number => {
    const value = getParam(params, key);
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        throw new Error(`missing required param: ${key}`);
    }
    return value;
};

const reqDefined = (params: unknown, key: string): unknown => {
    const value = getParam(params, key);
    if (value === undefined) {
        throw new Error(`missing required param: ${key}`);
    }
    return value;
};

const reqObj = (params: unknown, key: string): Record<string, unknown> => {
    const value = getParam(params, key);
    if (
        value === null ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        Object.keys(value).length === 0
    ) {
        throw new Error(`missing required param: ${key}`);
    }
    return value as Record<string, unknown>;
};

const qs = (parts: Record<string, string | number | undefined>): string => {
    const pairs = Object.entries(parts)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);
    return pairs.length > 0 ? `?${pairs.join('&')}` : '';
};

export const CAPABILITY_ENDPOINTS = {
    'adminUser.me': () => '/api/admin/admin-user',
    'memberships.list': () => '/api/admin/memberships',
    'membership.get': (params) =>
        `/api/admin/memberships/${reqStr(params, 'id')}`,
    'guides.list': () => '/api/admin/guides',
    'guide.get': (params) => `/api/admin/guides/${reqStr(params, 'id')}`,
    'guidanceSessions.search': (params) =>
        `/api/admin/guidance-session/search${qs({
            startDate: reqRaw(params, 'startDate'),
            endDate: reqRaw(params, 'endDate'),
        })}`,
    'guideCalendar.events': (params) =>
        `/api/admin/guides/calendar-events${qs({
            calendarId: reqRaw(params, 'calendarId'),
            timeMin: reqRaw(params, 'timeMin'),
            timeMax: reqRaw(params, 'timeMax'),
        })}`,
    'guidanceSession.transcript': (params) =>
        `/api/admin/guidance-session/${reqStr(params, 'id')}/transcript`,
    'membership.bills': (params) =>
        `/api/admin/memberships/${reqStr(params, 'membershipId')}/bills`,
    'membership.transactionsStats': (params) =>
        `/api/admin/memberships/${reqStr(params, 'membershipId')}/transactions-stats?monthsOfHistory=${optNum(params, 'monthsOfHistory', 12)}`,
    'membership.incomeSplitOverview': (params) =>
        `/api/admin/money-map/${reqStr(params, 'membershipId')}/income-split-overview`,
    'membership.complianceNotes': (params) =>
        `/api/admin/membership-compliance-notes/${reqStr(params, 'membershipId')}`,
    'membership.holisticSummary': (params) =>
        `/api/admin/memberships/${reqStr(params, 'membershipId')}/holistic-summary`,
    'membership.accounts': (params) =>
        `/api/admin/banking/accounts/membership/${reqStr(params, 'membershipId')}`,
    'membership.moneyMap': (params) =>
        `/api/admin/money-map/${reqStr(params, 'membershipId')}`,
    'membership.incomeSplitExecutions': (params) =>
        `/api/admin/money-map/${reqStr(params, 'membershipId')}/income-split-executions`,
    'membership.aiJobs': (params) =>
        `/api/admin/memberships/${reqStr(params, 'membershipId')}/fruitful-ai/jobs?includeFinished=${optNum(params, 'includeFinished', 0) ? 'true' : 'false'}`,
    'transactions.search': (params) =>
        `/api/admin/holistic/transactions/search${qs({
            membershipId: reqRaw(params, 'membershipId'),
            q: reqRaw(params, 'q'),
            limit: optNum(params, 'limit', 50),
            accountId: optStr(params, 'accountId'),
            startDate: optStr(params, 'startDate'),
            endDate: optStr(params, 'endDate'),
        })}`,
    'adminUsers.list': () => '/api/admin/admin-user/all',
    'user.history': (params) =>
        `/api/admin/users/${reqStr(params, 'id')}/history`,
    'investments.lnp': () => '/api/admin/investments/lnp',
    'investments.interventionProfiles': () =>
        '/api/admin/investments/intervention-profiles',
    'ictWithdrawalRequests.list': () => '/api/admin/ict-withdrawal-requests',
    'cashJournals.active': () => '/api/admin/cash-journals?mode=active',
    'slack.sendMessage': (params) => ({
        method: 'POST',
        path: '/api/internal/slack/send',
        body: {
            channel: reqRaw(params, 'channel'),
            text: reqRaw(params, 'text'),
        },
    }),
    'tools.geolocateIpAddress': (params) => ({
        method: 'POST',
        path: '/api/admin/fruit-slices/tools/geolocate-ip-address',
        body: {
            ipAddress: reqRaw(params, 'ipAddress'),
        },
    }),
    'tools.typesafeSystemOne': (params) => {
        const model = optStr(params, 'model');
        return {
            method: 'POST',
            path: '/api/admin/fruit-slices/tools/typesafe-system-one',
            body:
                model === undefined
                    ? {
                          state: reqDefined(params, 'state'),
                          questions: reqObj(params, 'questions'),
                      }
                    : {
                          state: reqDefined(params, 'state'),
                          questions: reqObj(params, 'questions'),
                          model,
                      },
        };
    },
    'tools.metabaseQuery': (params) => ({
        method: 'POST',
        path: '/api/admin/fruit-slices/tools/metabase-query',
        body: {
            databaseId: reqNum(params, 'databaseId'),
            sql: reqRaw(params, 'sql'),
        },
    }),
} satisfies Record<string, CapabilityEndpointResolver>;

export type ApiCapability = keyof typeof CAPABILITY_ENDPOINTS;
